import type { ResumeData } from '../types/resume';
const API_BASE = 'https://resume-builder-api.lasanahmed666.workers.dev';

export async function parseResume(text: string): Promise<{ data: import('../types/resume').ResumeData }> {
  const res = await fetch(`${API_BASE}/api/parse`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Parse failed' }));
    const error: any = new Error(err.error || 'Parse failed');
    error.detail = err.detail;
    error.extractedTextPreview = err.extractedTextPreview;
    throw error;
  }

  return res.json();
}

export async function parseResumeImage(base64Image: string): Promise<{ data: import('../types/resume').ResumeData }> {
  const res = await fetch(`${API_BASE}/api/parse`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ image: base64Image }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Image parse failed' }));
    const error: any = new Error(err.error || 'Image parse failed');
    error.detail = err.detail;
    throw error;
  }

  return res.json();
}

export async function exportPdf(html: string, template: string): Promise<Blob> {
  const res = await fetch(`${API_BASE}/api/export`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ html, template }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Export failed');
  }

  return res.blob();
}

export async function saveResume(userId: string, title: string, template: string, data: any) {
  const res = await fetch(`${API_BASE}/api/resumes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, title, template, data }),
  });
  return res.json();
}

export async function loadResume(id: string) {
  const res = await fetch(`${API_BASE}/api/resumes/${id}`);
  return res.json();
}


export async function analyzeResume(data: import('../types/resume').ResumeData): Promise<{ result: import('../types/resume').ResumeAnalysis }> {
  const res = await fetch(`${API_BASE}/api/ai/analyze`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ resume: data }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Analysis failed' }));
    throw new Error(err.error || 'Analysis failed');
  }

  return res.json();
}

export async function tailorResume(
  data: import('../types/resume').ResumeData,
  jobDescription: string
): Promise<{ result: import('../types/resume').TailorResult }> {
  const res = await fetch(`${API_BASE}/api/ai/tailor`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ resume: data, jobDescription }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Tailoring failed' }));
    throw new Error(err.error || 'Tailoring failed');
  }

  return res.json();
}


export type GenerateType =
  | 'summary' | 'experience' | 'project' | 'skills'
  | 'expand' | 'shorten' | 'tone-shift' | 'quantify'
  | 'linkedin-about' | 'cert-suggestions' | 'translate' | 'jd-skills';

export interface GenerateOptions {
  type: GenerateType;
  resume: import('../types/resume').ResumeData;
  context?: string;
  prompt?: string;
  tone?: string;
  language?: string;
}

export async function generateWithAI(options: GenerateOptions): Promise<{ result: string | string[] }> {
  const res = await fetch(`${API_BASE}/api/ai/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(options),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Generation failed' }));
    throw new Error(err.error || 'Generation failed');
  }

  return res.json();
}

export async function generateCoverLetter(
  data: import('../types/resume').ResumeData,
  jobDescription: string
): Promise<{ result: string }> {
  const res = await fetch(`${API_BASE}/api/ai/cover-letter`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ resume: data, jobDescription }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Cover letter generation failed' }));
    throw new Error(err.error || 'Cover letter generation failed');
  }

  return res.json();
}

export interface InterviewPrepResult {
  questions: Array<{ question: string; suggestedAnswer: string }>;
}

export async function generateInterviewPrep(
  data: import('../types/resume').ResumeData,
  jobDescription?: string
): Promise<{ result: InterviewPrepResult }> {
  const res = await fetch(`${API_BASE}/api/ai/interview-prep`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ resume: data, jobDescription }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Interview prep failed' }));
    throw new Error(err.error || 'Interview prep failed');
  }

  return res.json();
}

export interface AtsScanResult {
  score: number;
  presentKeywords: string[];
  missingKeywords: string[];
  reasoning: string;
}

export async function scanAts(
  data: import('../types/resume').ResumeData,
  jobDescription: string
): Promise<{ result: AtsScanResult }> {
  const res = await fetch(`${API_BASE}/api/ai/ats-scan`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ resume: data, jobDescription }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'ATS scan failed' }));
    throw new Error(err.error || 'ATS scan failed');
  }

  return res.json();
}

export async function translateResume(
  data: import('../types/resume').ResumeData,
  language: string
): Promise<{ result: string }> {
  const res = await fetch(`${API_BASE}/api/ai/translate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ resume: data, language }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Translation failed' }));
    throw new Error(err.error || 'Translation failed');
  }

  return res.json();
}

export async function generateLinkedIn(
  data: ResumeData,
  focus?: string
): Promise<{ result: { headline: string; about: string; skills: string[] } }> {
  const res = await fetch(`${API_BASE}/api/ai/linkedin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ resume: data, focus }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'LinkedIn generation failed' }));
    throw new Error(err.error || 'LinkedIn generation failed');
  }

  return res.json();
}

export async function generatePortfolio(
  data: ResumeData,
  theme?: string
): Promise<{ result: string }> {
  const res = await fetch(`${API_BASE}/api/ai/portfolio`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ resume: data, theme }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Portfolio generation failed' }));
    throw new Error(err.error || 'Portfolio generation failed');
  }

  return res.json();
}
